// BL-2437 (ADR 033 — Transition Service): the Draft ↔ Upcoming rule over the four facts a phone
// holds offline (lot phase kind, home lot or not, auction phase kind, target). The server's batch
// check reaches it through `verdictForBatch`, and the offline check calls it directly: one rule.
import {
  canSetLotStatus,
  isLotStatusAllowed,
  type AuctionPhaseKind,
  type BatchApplyVerdict,
  type LotPhaseKind,
} from '@auctogon/core/isomorphic/lifecycle';

/**
 * BUG-2507 — the sentence for the `auction-opened` refusal: the server's inline error and the
 * status menu's greyed Upcoming row read this one string, so the two cannot drift.
 */
export const AUCTION_OPENED_UPCOMING_MESSAGE = 'The auction has opened. Move the lot to Draft instead.';

/** The inputs of {@link setStatusVerdict}. */
export interface SetStatusVerdictInput {
  /** The lot's ADR 023 phase kind; `null` when it has none (a live-webcast lot). */
  lotPhaseKind: LotPhaseKind | null;
  /** The lot is in no auction (ADR 028's home lot). */
  isHomeLot: boolean;
  /** The parent auction's phase kind; `null` for a home lot (no ceiling applies). */
  auctionPhaseKind: AuctionPhaseKind | null;
  /** The status asked for. */
  target: 'draft' | 'ready';
}

/**
 * Whether a lot may be set to Draft or Upcoming. Order is the server's: no derivable phase
 * (`unsupported-live`), then a home lot's Upcoming (`not-in-auction`, BUG-1110), then the matrix's
 * terminal and ceiling rungs (`canSetLotStatus`), then Upcoming under an opened auction
 * (`auction-opened`, BUG-2507). Bids are not an input (BUG-1530).
 *
 * The last rung exists because the auto-promote rule re-opens a `ready` lot under an `active` or
 * `paused` auction, so a lot that is already past Upcoming can never land back there (matrix §7ap):
 * the move was a 200 that changed nothing. A Draft or Upcoming lot is left alone, because there the
 * same promotion is a real change that BL-1056 / BL-1299 rely on (the lot goes live, close floored).
 * It ranks below `terminal`, whose remedy (reverse the sale) is the one a sold lot needs.
 * `accepting-pre-bids` is untouched: Upcoming genuinely lands there (matrix §7s).
 *
 * @param input - See {@link SetStatusVerdictInput}.
 * @returns The verdict.
 */
export function setStatusVerdict(input: SetStatusVerdictInput): BatchApplyVerdict {
  if (input.lotPhaseKind == null) return { allowed: false, reason: 'unsupported-live' };
  if (input.isHomeLot && input.target === 'ready') return { allowed: false, reason: 'not-in-auction' };
  const auctionAllowsTargetStatus =
    input.auctionPhaseKind == null ? true : isLotStatusAllowed(input.target, input.auctionPhaseKind);
  const verdict = canSetLotStatus({ lotPhaseKind: input.lotPhaseKind, auctionAllowsTargetStatus });
  if (!verdict.allowed) return verdict;
  if (input.target === 'ready' && isOpenedAuction(input.auctionPhaseKind) && !isPrepPhase(input.lotPhaseKind)) {
    return { allowed: false, reason: 'auction-opened' };
  }
  return verdict;
}

/** An auction whose `ready` lots the auto-promote rule turns into open ones (`resolveAutoPromoteOnSave`). */
function isOpenedAuction(phase: AuctionPhaseKind | null): boolean {
  return phase === 'active' || phase === 'paused';
}

/** A lot that has not yet opened: Draft or Upcoming. */
function isPrepPhase(phase: LotPhaseKind): boolean {
  return phase === 'draft' || phase === 'ready';
}
